import { LanguagesEnum } from '@/api/enums';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { Course } from '@/client/courses';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants/routes';
import { LanguageContext } from '@/common/context/LanguageContext';
import { TranslationFunctionType } from '@/common/types';
import { ReplaceScheduledContent } from '@/components/courses/customization/ReplaceScheduledContent';
import { useCourse } from '@/hooks/query/courses.hooks';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import { getTranslationByLanguage } from '@/utils/helpers';
import { Card } from 'primereact/card';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CoursesTabs } from './CoursesTabs';

const PageContainer = styled(Card)`
  &.p-card {
    background: var(--beige-main);
    border-radius: var(--semi-medium-border-radius);
    margin-top: 16px;
  }

  .p-card-body {
    padding: 42px;
  }
`;

const ContentContainer = styled(FlexContainer)`
  background: var(--beige-light, #f6f6f3);
  border-radius: 19px;
  padding: 42px;
  width: 100%;
`;

const getBreadcrumbs = (
  account: Account,
  customizedCourse: Course,
  language: LanguagesEnum,
  t: TranslationFunctionType,
): MenuItem[] => {
  return [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('content'),
      url: RedirectPaths[RedirectPathsEnum.COURSES](),
      template: AppBreadCrumbTemplate,
    },
    {
      label: getTranslationByLanguage(
        customizedCourse?.courseTranslations,
        language as LanguagesEnum,
        customizedCourse.defaultLanguage,
      )?.name,
      url: RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](
        customizedCourse.id,
      ),
      template: AppBreadCrumbTemplate,
      className: 'active',
    },
  ];
};

export const ReplaceScheduledContentPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const { customizedCourseId } = useParams<{
    customizedCourseId: string;
  }>();
  const { language } = useContext(LanguageContext);

  const { course: customizedCourse, isLoading: isLoadingCustomized } =
    useCourse({
      courseId: customizedCourseId,
      enabled: !!customizedCourseId,
    });

  const originalCourseId =
    customizedCourse?.customizationMetadata?.originalContentId;

  const { course: originalCourse, isLoading: isLoadingOriginal } = useCourse({
    courseId: originalCourseId,
    enabled: !!originalCourseId,
  });

  const isLoading = isLoadingOriginal || isLoadingCustomized;

  const handleCancel = () => {
    navigate(
      RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](
        customizedCourseId || '',
      ),
    );
  };

  const handleGoToTrainingPlan = () => {
    navigate(RedirectPaths[RedirectPathsEnum.ACCOUNT_COURSE_PLANNER]());
  };

  if (isLoading || !originalCourse || !customizedCourse || !currentAccount) {
    return (
      <FlexContainer direction="column" className="mt-5">
        <ProgressSpinner />
        <h3>{t('content.loading')}</h3>
      </FlexContainer>
    );
  }

  return (
    <>
      <AppBreadCrumb
        model={getBreadcrumbs(
          currentAccount as Account,
          customizedCourse,
          customizedCourse.defaultLanguage,
          t,
        )}
      />

      <h2 className="m-0">
        {
          getTranslationByLanguage(
            customizedCourse?.courseTranslations,
            language as LanguagesEnum,
            customizedCourse.defaultLanguage,
          )?.name
        }
      </h2>

      {customizedCourse && <CoursesTabs course={customizedCourse} />}

      <PageContainer>
        <ContentContainer direction="column" gap={32} align="center">
          <ReplaceScheduledContent
            accountId={currentAccount.id}
            originalCourse={originalCourse}
            customizedCourse={customizedCourse}
            onCancel={handleCancel}
            onGoToTrainingPlan={handleGoToTrainingPlan}
          />
        </ContentContainer>
      </PageContainer>
    </>
  );
};
